import {
  PreclassTestCategoryType,
  PreclassTestNameType,
  PreclassTestOutcomeType,
  PreclassTestResultType,
  PreclassTestSpecificErrorsType,
} from "../../../shared/types/preclass_check";

import { PreclassCheckSettings } from "./settings";

import { BaseLogger } from "base_logger";
import { HardwareMonitor } from "contexts/hardware_monitor_context";
import { CpuModelAndScoreType } from "shared/types/hardware_monitor";

class ComputerTests extends BaseLogger {
  static async testComputer(hardwareMonitor: HardwareMonitor) {
    ComputerTests.log("testComputer: Starting computer tests");
    const testResults = await ComputerTests.testCPU(hardwareMonitor);
    testResults.push(await ComputerTests.testMemory(hardwareMonitor));
    return testResults;
  }

  static async testCPU(hardwareMonitor: HardwareMonitor): Promise<PreclassTestResultType[]> {
    const stats = (await hardwareMonitor.gatherCpuInfo()) as CpuModelAndScoreType;
    const testResults: PreclassTestResultType[] = [];
    if (stats && stats.geekbench && stats.geekbench.found) {
      if (stats.geekbench["multi-score"] >= PreclassCheckSettings.MIN_GEEKBENCH_SCORE) {
        ComputerTests.log(
          `testCPU: Sufficient geekbench score detected, score=${stats.geekbench["multi-score"]}`,
        );

        testResults.push({
          name: PreclassTestNameType.CPU_SCORE,
          category: PreclassTestCategoryType.HARDWARE,
          outcome: PreclassTestOutcomeType.SUCCESS,
          data: { stats },
        });
      } else {
        ComputerTests.warn(
          `testCPU: Insufficient geekbench score detected, score=${
            stats && stats.geekbench["multi-score"]
          }`,
        );
        testResults.push({
          name: PreclassTestNameType.CPU_SCORE,
          category: PreclassTestCategoryType.HARDWARE,
          outcome: PreclassTestOutcomeType.FAIL,
          error: PreclassTestSpecificErrorsType.LOW_CPU_SCORE,
          data: { stats },
        });
      }
    }

    if (!navigator.hardwareConcurrency) {
      ComputerTests.warn("testCPU: hardwareConcurrency is undefined");
      testResults.push({
        name: PreclassTestNameType.CPU_CORES,
        category: PreclassTestCategoryType.HARDWARE,
        outcome: PreclassTestOutcomeType.NA,
      });
    } else {
      const cpus = navigator.hardwareConcurrency;

      const minCpusAccepted = PreclassCheckSettings.IDEAL_NUMBER_OF_CORES;
      if (cpus >= minCpusAccepted) {
        ComputerTests.log(`CPUCoreNumTest: Sufficient cores detected, cpus=${cpus}`);
        testResults.push({
          name: PreclassTestNameType.CPU_CORES,
          category: PreclassTestCategoryType.HARDWARE,
          outcome: PreclassTestOutcomeType.SUCCESS,
          data: { cpus },
        });
      } else {
        ComputerTests.error(`CPUCoreNumTest: Insufficient number of cores, cpus=${cpus}`);
        testResults.push({
          name: PreclassTestNameType.CPU_CORES,
          category: PreclassTestCategoryType.HARDWARE,
          outcome: PreclassTestOutcomeType.FAIL,
          error: PreclassTestSpecificErrorsType.LOW_CPU_CORES,
          data: { cpus },
        });
      }
    }
    return testResults;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static async testMemory(hardwareMonitor: HardwareMonitor): Promise<PreclassTestResultType> {
    const result = await HardwareMonitor.getMemoryInfo();
    if (!result || !result.totalInGB) {
      ComputerTests.log("TotalMemoryTest: Memory was not detected");
      return {
        name: PreclassTestNameType.TOTAL_MEMORY,
        category: PreclassTestCategoryType.HARDWARE,
        outcome: PreclassTestOutcomeType.NA,
      };
    }

    const memory = result.totalInGB;
    if (memory >= PreclassCheckSettings.MIN_MEMORY_GB) {
      ComputerTests.log(`testMemory: Sufficient memory detected, memory=${memory}`);
      return {
        name: PreclassTestNameType.TOTAL_MEMORY,
        category: PreclassTestCategoryType.HARDWARE,
        outcome: PreclassTestOutcomeType.SUCCESS,
        data: { memory },
      };
    }
    ComputerTests.warn(`testMemory: Insufficient memory detected, memory=${memory}`);
    return {
      name: PreclassTestNameType.TOTAL_MEMORY,
      category: PreclassTestCategoryType.HARDWARE,
      outcome: PreclassTestOutcomeType.FAIL,
      error: PreclassTestSpecificErrorsType.LOW_MEMORY,
      data: { memory },
    };
  }
}

export { ComputerTests };
